import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';

import BasicSEO from '../../Components/SEO/BasicSEO';

import {
  Container,
  Grid,
  Paper,
  Typography,
  Box,
  TextField,
  Button,
  Avatar,
  IconButton,
  Chip,
  MenuItem,
  useMediaQuery,
  useTheme,
  Stepper,
  Step,
  StepLabel,
  SwipeableDrawer,
  AppBar,
  Toolbar,
  Fab,
  Collapse,
  Card,
  CardContent
} from '@mui/material';
import {
  PhotoCamera as PhotoCameraIcon,
  SearchOutlined as SearchIcon,
  BusinessCenter as BusinessIcon,
  Psychology as AIIcon,
  AutoAwesome as AutoAwesomeIcon,
  RateReview as ReviewIcon,
  People as PeopleIcon,
  Recommend as RecommendIcon,
  CheckCircle as CheckCircleIcon,
  TrendingUp as TrendingIcon,
  QuestionAnswer as FAQIcon,
  Schedule as ScheduleIcon,
  Google as GoogleIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Menu as MenuIcon,
  Close as CloseIcon,
  Info as InfoIcon,
  AccountCircle as ProfileIcon,
  Lightbulb as BenefitsIcon
} from '@mui/icons-material';

import PlacesAutocomplete from 'react-places-autocomplete';
import { useGetPreSignedUrlQuery } from '../../redux/thunks/files/filespresignurl';
import { useGetUserProfileQuery, useUpdateUserProfileMutation } from '../../redux/thunks/users/userprofile';
import { DatePicker } from '@mui/x-date-pickers';
import { useGetUserByEmailQuery } from '../../redux/thunks/users/getuserbyemail';
import { useSetAffiliateAttributionMutation } from '../../redux/thunks/sp/affiliate';
import { useSetMarketingAttributionMutation } from '../../redux/thunks/sp/marketing';
import { useSetPreviewAttributionMutation } from '../../redux/thunks/sp/preview';


// ===== Two goals only =====
const USER_GOALS = [
  {
    id:          'discover',
    icon:        SearchIcon,
    title:       'Discover Businesses',
    description: 'Find trusted local businesses through your network and write verified reviews',
  },
  {
    id:          'business',
    icon:        BusinessIcon,
    title:       'Manage My Business',
    description: 'Automate content, respond to reviews and grow your business with AI',
  },
];

// ===== Capabilities shown in left column =====
const DISCOVERY_CAPABILITIES = [
  { icon: SearchIcon,    text: 'Find trusted businesses through your network' },
  { icon: ReviewIcon,    text: 'Write verified reviews that matter' },
  { icon: PeopleIcon,    text: 'Build your personal trust network' },
  { icon: RecommendIcon, text: 'Get personalized local recommendations' },
  { icon: TrendingIcon,  text: 'See what your connections recommend' },
];

const BUSINESS_CAPABILITIES = [
  { icon: AutoAwesomeIcon, text: 'AI generates social posts, FAQs and blog content' },
  { icon: ReviewIcon,      text: 'Automated review response drafts' },
  { icon: GoogleIcon,      text: 'Google Reviews integration' },
  { icon: FAQIcon,         text: 'FAQ management from customer questions' },
  { icon: ScheduleIcon,    text: 'Scheduled content for Facebook, Instagram, LinkedIn' },
  { icon: TrendingIcon,    text: 'Business performance insights' },
];

// ===== Journey steps shown in right column =====
const DISCOVERY_JOURNEY = [
  { step: 1, label: 'Complete your profile',       current: true  },
  { step: 2, label: 'Find your network',            current: false },
  { step: 3, label: 'Discover businesses',          current: false },
  { step: 4, label: 'Write your first review',      current: false },
];

const BUSINESS_JOURNEY = [
  { step: 1, label: 'Complete your profile',                    current: true  },
  { step: 2, label: 'Add your business',                        current: false },
  { step: 3, label: 'AI learns your business context',          current: false },
  { step: 4, label: 'Content drafts generated automatically',   current: false },
  { step: 5, label: 'Approve and publish with one click',       current: false },
];

// ===== Skeleton components =====
const ProfilePreviewSkeleton = () => (
  <Paper variant="outlined" sx={{ p: 2, border: 'none' }}>
    <Box sx={{ width: 120, height: 120, borderRadius: '50%', bgcolor: 'grey.200', mx: 'auto', mb: 2 }} />
    <Box sx={{ height: 20, bgcolor: 'grey.200', borderRadius: 1, mb: 1, width: '60%', mx: 'auto' }} />
    <Box sx={{ height: 16, bgcolor: 'grey.200', borderRadius: 1, width: '40%', mx: 'auto' }} />
  </Paper>
);

// ===== Left Column — reacts to goal selection =====
const CapabilitiesPanel = ({ isBusiness, profileImage, formData, handleImageUpload }) => {
  const capabilities = isBusiness ? BUSINESS_CAPABILITIES : DISCOVERY_CAPABILITIES;
  const title        = isBusiness ? 'What you unlock' : 'What you can do';
  const color        = isBusiness ? 'primary.main' : 'success.main';
  const bgColor      = isBusiness ? 'primary.50' : 'success.50';

  return (
    <Box>
      {/* Profile photo preview */}
      <Paper variant="outlined" sx={{ p: 2, border: 'none', textAlign: 'center', mb: 2 }}>
        <Box sx={{ position: 'relative', width: 120, height: 120, mx: 'auto', mb: 2 }}>
          <Avatar src={profileImage} sx={{ width: 120, height: 120 }} />
          <IconButton
            sx={{
              position:        'absolute',
              bottom:          0,
              right:           0,
              backgroundColor: 'primary.main',
              '&:hover':       { backgroundColor: 'primary.dark' }
            }}
            component="label"
          >
            <input hidden accept="image/*" type="file" onChange={handleImageUpload} />
            <PhotoCameraIcon sx={{ color: 'white' }} />
          </IconButton>
        </Box>
        <Typography variant="subtitle1">
          {formData.name
            ? `${formData.name}${formData.lastName ? ' ' + formData.lastName : ''}`
            : 'Your Name'
          }
        </Typography>
        <Typography variant="body2" color="text.secondary">
          New Member
        </Typography>
      </Paper>

      {/* Capabilities list */}
      <Paper variant="outlined" sx={{ p: 2, border: 'none' }}>
        <Typography variant="h6" gutterBottom>
          {title}
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 1 }}>
          {capabilities.map((item, index) => {
            const Icon = item.icon;
            return (
              <Box
                key={index}
                sx={{
                  display:    'flex',
                  alignItems: 'center',
                  gap:        1.5,
                  p:          1.5,
                  bgcolor:    bgColor,
                  borderRadius: 1
                }}
              >
                <Icon sx={{ color, fontSize: 20, flexShrink: 0 }} />
                <Typography variant="body2">{item.text}</Typography>
              </Box>
            );
          })}
        </Box>
      </Paper>
    </Box>
  );
};

// ===== Right Column — reacts to goal selection =====
const JourneyPanel = ({ isBusiness }) => {
  const journey = isBusiness ? BUSINESS_JOURNEY : DISCOVERY_JOURNEY;
  const title   = isBusiness ? 'Your business journey' : 'Your journey';
  const accent  = isBusiness ? 'primary' : 'success';

  return (
    <Paper variant="outlined" sx={{ p: 2, border: 'none' }}>
      <Typography variant="h6" gutterBottom>
        {title}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Here is what happens after you complete this page
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
        {journey.map((item, index) => (
          <Box key={item.step} sx={{ display: 'flex', gap: 2 }}>
            {/* Step indicator + connector line */}
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <Box sx={{
                width:          32,
                height:         32,
                borderRadius:   '50%',
                bgcolor:        item.current ? `${accent}.main` : 'grey.200',
                color:          item.current ? 'white' : 'text.secondary',
                display:        'flex',
                alignItems:     'center',
                justifyContent: 'center',
                fontWeight:     700,
                fontSize:       '0.85rem',
                flexShrink:     0
              }}>
                {item.current ? '→' : item.step}
              </Box>
              {index < journey.length - 1 && (
                <Box sx={{ width: 2, flex: 1, bgcolor: 'grey.200', my: 0.5, minHeight: 16 }} />
              )}
            </Box>

            {/* Step label */}
            <Box sx={{ pb: index < journey.length - 1 ? 1.5 : 0, pt: 0.5 }}>
              <Typography
                variant="body2"
                fontWeight={item.current ? 700 : 400}
                color={item.current ? `${accent}.main` : 'text.secondary'}
              >
                {item.label}
              </Typography>
              {item.current && (
                <Typography variant="caption" color="text.disabled">
                  You are here
                </Typography>
              )}
            </Box>
          </Box>
        ))}
      </Box>
    </Paper>
  );
};

// ===== Goal Selector =====
const GoalSelector = ({ selectedGoals, onSelect, error }) => (
  <Box sx={{ mb: 4 }}>
    <Typography variant="h6" gutterBottom>
      What brings you to Revfer?
    </Typography>
    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
      Choose one or both
    </Typography>

    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {USER_GOALS.map((goal) => {
        const Icon       = goal.icon;
        const isSelected = selectedGoals.includes(goal.id);

        return (
          <Paper
            key={goal.id}
            variant="outlined"
            onClick={() => onSelect(goal.id)}
            sx={{
              p:           2,
              cursor:      'pointer',
              border:      '2px solid',
              borderColor: isSelected ? 'primary.main' : 'divider',
              bgcolor:     isSelected ? 'primary.50' : 'background.paper',
              transition:  'all 0.2s ease',
              '&:hover':   {
                borderColor: 'primary.main',
                transform:   'translateY(-1px)',
                boxShadow:   2
              }
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
              <Box sx={{
                width:          44,
                height:         44,
                borderRadius:   2,
                bgcolor:        isSelected ? 'primary.main' : 'grey.100',
                display:        'flex',
                alignItems:     'center',
                justifyContent: 'center',
                flexShrink:     0
              }}>
                <Icon sx={{ color: isSelected ? 'white' : 'text.secondary', fontSize: 22 }} />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle1" fontWeight={600}>
                  {goal.title}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {goal.description}
                </Typography>
              </Box>
              {isSelected && (
                <CheckCircleIcon color="primary" sx={{ flexShrink: 0, mt: 0.5 }} />
              )}
            </Box>
          </Paper>
        );
      })}
    </Box>

    {error && (
      <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
        {error}
      </Typography>
    )}
  </Box>
);

// ===== Mobile Info Sidebar =====
const MobileInfoSidebar = ({ isOpen, onClose, profileImage, formData, navigate, isBusiness }) => {
  const [expandedSections, setExpandedSections] = useState({
    profile:      true,
    capabilities: false,
    journey:      false
  });

  const toggleSection = (section) =>
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));

  const capabilities = isBusiness ? BUSINESS_CAPABILITIES : DISCOVERY_CAPABILITIES;
  const journey      = isBusiness ? BUSINESS_JOURNEY      : DISCOVERY_JOURNEY;
  const accent       = isBusiness ? 'primary'             : 'success';

  return (
    <SwipeableDrawer
      anchor="right"
      open={isOpen}
      onClose={onClose}
      onOpen={() => {}}
      PaperProps={{ sx: { width: '100%', maxWidth: 400, bgcolor: 'background.default' } }}
    >
      <AppBar position="static" color="default" elevation={0}>
        <Toolbar>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>Profile & Info</Typography>
          <IconButton onClick={onClose}><CloseIcon /></IconButton>
        </Toolbar>
      </AppBar>

      <Box sx={{ p: 2, overflow: 'auto', height: '100%' }}>

        {/* Profile Preview */}
        <Card sx={{ mb: 2 }}>
          <CardContent
            sx={{ pb: 1, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
            onClick={() => toggleSection('profile')}
          >
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <ProfileIcon sx={{ mr: 1, color: 'primary.main' }} />
              <Typography variant="h6">Profile Preview</Typography>
            </Box>
            {expandedSections.profile ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </CardContent>
          <Collapse in={expandedSections.profile}>
            <CardContent sx={{ pt: 0, textAlign: 'center' }}>
              <Box sx={{ position: 'relative', width: 100, height: 100, mx: 'auto', mb: 2 }}>
                <Avatar src={profileImage} sx={{ width: 100, height: 100 }} />
              </Box>
              <Typography variant="subtitle1">
                {formData.name || 'Your Name'}
              </Typography>
              <Typography variant="body2" color="text.secondary">New Member</Typography>
              <Chip
                label={formData.selectedGoals.length > 0
                  ? formData.selectedGoals.map(g => USER_GOALS.find(ug => ug.id === g)?.title).join(' + ')
                  : 'No goal selected yet'
                }
                size="small"
                color={formData.selectedGoals.length > 0 ? 'primary' : 'default'}
                sx={{ mt: 1 }}
              />
            </CardContent>
          </Collapse>
        </Card>

        {/* Capabilities */}
        <Card sx={{ mb: 2 }}>
          <CardContent
            sx={{ pb: 1, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
            onClick={() => toggleSection('capabilities')}
          >
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <BenefitsIcon sx={{ mr: 1, color: 'primary.main' }} />
              <Typography variant="h6">{isBusiness ? 'What you unlock' : 'What you can do'}</Typography>
            </Box>
            {expandedSections.capabilities ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </CardContent>
          <Collapse in={expandedSections.capabilities}>
            <CardContent sx={{ pt: 0 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {capabilities.map((item, index) => {
                  const Icon = item.icon;
                  return (
                    <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Icon sx={{ color: `${accent}.main`, fontSize: 18, flexShrink: 0 }} />
                      <Typography variant="body2">{item.text}</Typography>
                    </Box>
                  );
                })}
              </Box>
            </CardContent>
          </Collapse>
        </Card>

        {/* Journey */}
        <Card sx={{ mb: 2 }}>
          <CardContent
            sx={{ pb: 1, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
            onClick={() => toggleSection('journey')}
          >
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <InfoIcon sx={{ mr: 1, color: 'primary.main' }} />
              <Typography variant="h6">{isBusiness ? 'Your business journey' : 'Your journey'}</Typography>
            </Box>
            {expandedSections.journey ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </CardContent>
          <Collapse in={expandedSections.journey}>
            <CardContent sx={{ pt: 0 }}>
              {journey.map((item, index) => (
                <Box key={item.step} sx={{ display: 'flex', gap: 1.5, mb: index < journey.length - 1 ? 1.5 : 0 }}>
                  <Box sx={{
                    width:          24,
                    height:         24,
                    borderRadius:   '50%',
                    bgcolor:        item.current ? `${accent}.main` : 'grey.200',
                    color:          item.current ? 'white' : 'text.secondary',
                    display:        'flex',
                    alignItems:     'center',
                    justifyContent: 'center',
                    fontSize:       '0.75rem',
                    fontWeight:     700,
                    flexShrink:     0
                  }}>
                    {item.current ? '→' : item.step}
                  </Box>
                  <Typography
                    variant="body2"
                    fontWeight={item.current ? 700 : 400}
                    color={item.current ? `${accent}.main` : 'text.secondary'}
                    sx={{ pt: 0.25 }}
                  >
                    {item.label}
                  </Typography>
                </Box>
              ))}
            </CardContent>
          </Collapse>
        </Card>

      </Box>
    </SwipeableDrawer>
  );
};

// ===== Mobile Step Progress =====
const MobileStepProgress = ({ currentStep }) => {
  const steps = ['Goals', 'Your Info', 'Done'];
  return (
    <Paper sx={{ p: 2, mb: 2, bgcolor: 'primary.main' }}>
      <Stepper activeStep={currentStep} orientation="horizontal" sx={{ mb: 1 }}>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel sx={{ '& .MuiStepLabel-label': { color: 'rgba(255,255,255,0.8)', fontSize: '0.75rem' } }}>
              {label}
            </StepLabel>
          </Step>
        ))}
      </Stepper>
      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
        Step {currentStep + 1} of {steps.length}
      </Typography>
    </Paper>
  );
};

// ===== Main Component =====
export default function OnboardingPage() {
  const navigate  = useNavigate();
  const theme     = useTheme();
  const isMobile  = useMediaQuery(theme.breakpoints.down('md'));

  const [sidebarOpen,       setSidebarOpen]       = useState(false);
  const [currentMobileStep, setCurrentMobileStep] = useState(0);
  const [goalError,         setGoalError]         = useState('');
  const [locationSelected,  setLocationSelected]  = useState(false);

  const user   = useSelector((state) => state.auth?.user || '');
  const userId = useSelector((state) => state.auth?.userId || null);

  const [loading,    setLoading]    = useState(false);
  const [formData,   setFormData]   = useState({
    name:          '',
    lastName:      '',
    location:      '',
    selectedGoals: [],
    photo:         '',
    dob:           new Date(),
    gender:        '',
  });
  const [profileImage, setProfileImage] = useState(null);

  const args = { eventType: 'UserProfilePhoto', userId };

  const { data: userData,        isLoading: isUserLoading }        = useGetUserByEmailQuery(user.email);
  const { data: presignedUrlData, isLoading: isPresignedUrlLoading } = useGetPreSignedUrlQuery(args);
  const [editProfile, { isLoading: ProfileEditSaving }]             = useUpdateUserProfileMutation();
  const [setAffiliateAttribution] = useSetAffiliateAttributionMutation();
  const [setPreviewAttribution] = useSetPreviewAttributionMutation();
  const [setMarketingAttribution] = useSetMarketingAttributionMutation();



  

  // Derive isBusiness from selection
  const isBusiness = formData.selectedGoals.includes('business');

  useEffect(() => {
    if (user?.firstName) setFormData(prev => ({ ...prev, name:     user.firstName }));
    if (user?.lastName)  setFormData(prev => ({ ...prev, lastName: user.lastName  }));
  }, [user]);

  // Affiliate attribution promotion — runs once when user is available.
  // Reads localStorage, calls backend, clears on success. Idempotent
  // across pages; safe to also run from HomePage.
  useEffect(() => {
    if (!userId) return;

    const raw = localStorage.getItem('revfer_ref');
    if (!raw) return;

    let attribution;
    try {
      attribution = JSON.parse(raw);
    } catch {
      localStorage.removeItem('revfer_ref');
      return;
    }

    if (!attribution?.code || !attribution?.captured_at) {
      localStorage.removeItem('revfer_ref');
      return;
    }

    // Client-side expiry check (server validates too)
    if (attribution.expires_at) {
      try {
        if (new Date(attribution.expires_at).getTime() < Date.now()) {
          localStorage.removeItem('revfer_ref');
          return;
        }
      } catch {
        // Ignore parse error, let server decide
      }
    }

    setAffiliateAttribution({
      code: attribution.code,
      captured_at: attribution.captured_at
    })
      .unwrap()
      .then(() => {
        localStorage.removeItem('revfer_ref');
        document.cookie = 'revfer_ref=; Max-Age=0; Path=/; SameSite=Lax; Secure';
      })
      .catch((err) => {
        console.warn('Affiliate attribution call failed, will retry', err);
      });
  }, [userId, setAffiliateAttribution]);

// Marketing attribution promotion — mirrors the affiliate/preview effects.
// Reads revfer_mktg (UTMs + click ids captured on /m), sends to backend, clears on success.
// Independent of the other two: a user can arrive via an ad AND have an affiliate/preview ref.
useEffect(() => {
  if (!userId) return;

  const raw = localStorage.getItem('revfer_mktg');
  if (!raw) return;

  let attribution;
  try {
    attribution = JSON.parse(raw);
  } catch {
    localStorage.removeItem('revfer_mktg');
    return;
  }

  if (!attribution?.utm_source || !attribution?.captured_at) {
    localStorage.removeItem('revfer_mktg');
    return;
  }

  if (attribution.expires_at) {
    try {
      if (new Date(attribution.expires_at).getTime() < Date.now()) {
        localStorage.removeItem('revfer_mktg');
        return;
      }
    } catch {
      // ignore parse error, let server decide
    }
  }

  setMarketingAttribution({
    utm_source:   attribution.utm_source,
    utm_medium:   attribution.utm_medium   || null,
    utm_campaign: attribution.utm_campaign || null,
    utm_content:  attribution.utm_content  || null,
    utm_term:     attribution.utm_term     || null,
    click_ids:    attribution.click_ids    || null,
    captured_at:  attribution.captured_at,
  })
    .unwrap()
    .then(() => {
      localStorage.removeItem('revfer_mktg');
    })
    .catch((err) => {
      console.warn('Marketing attribution call failed, will retry', err);
    });
}, [userId, setMarketingAttribution]);

  // Preview attribution promotion — mirrors the affiliate effect above.
  // Reads revfer_preview_ref, maps the preview slug to this user, clears on success.
  // Independent of affiliate attribution: a user can have BOTH (referred by an
  // affiliate AND arrived via a preview) — separate keys, separate writes.
  useEffect(() => {
    if (!userId) return;

    const raw = localStorage.getItem('revfer_preview_ref');
    if (!raw) return;

    let attribution;
    try {
      attribution = JSON.parse(raw);
    } catch {
      localStorage.removeItem('revfer_preview_ref');
      return;
    }

    if (!attribution?.slug || !attribution?.captured_at) {
      localStorage.removeItem('revfer_preview_ref');
      return;
    }

    if (attribution.expires_at) {
      try {
        if (new Date(attribution.expires_at).getTime() < Date.now()) {
          localStorage.removeItem('revfer_preview_ref');
          return;
        }
      } catch {
        // ignore parse error, let server decide
      }
    }

    setPreviewAttribution({
      slug: attribution.slug,
      captured_at: attribution.captured_at
    })
      .unwrap()
      .then(() => {
        localStorage.removeItem('revfer_preview_ref');
        document.cookie = 'revfer_preview_ref=; Max-Age=0; Path=/; SameSite=Lax; Secure';
      })
      .catch((err) => {
        console.warn('Preview attribution call failed, will retry', err);
      });
  }, [userId, setPreviewAttribution]);

  const uploadPhotoToS3 = async (file, presignedUrl) => {
    const response = await fetch(presignedUrl, {
      method:  'PUT',
      body:    file,
      headers: { 'Content-Type': file.type, 'x-amz-meta-eventtype': 'UserProfilePhoto' }
    });
    if (!response.ok) throw new Error('Failed to upload photo');
    return true;
  };

  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (file) {
      try {
        const reader  = new FileReader();
        reader.onload = (e) => setProfileImage(e.target.result);
        reader.readAsDataURL(file);
        if (presignedUrlData?.url) {
          await uploadPhotoToS3(file, presignedUrlData.url);
          const photoUrl = presignedUrlData.url.split('?')[0];
          setFormData(prev => ({ ...prev, photo: photoUrl }));
        }
      } catch (error) {
        console.error('Error uploading photo:', error);
      }
    }
  };

  const handleGoalSelection = (goalId) => {
    setGoalError('');
    setFormData(prev => ({
      ...prev,
      selectedGoals: prev.selectedGoals.includes(goalId)
        ? prev.selectedGoals.filter(id => id !== goalId)
        : [...prev.selectedGoals, goalId]
    }));
  };

  const handleAddressChange = (address) => {
    setFormData(prev => ({ ...prev, location: address }));
    if (locationSelected) setLocationSelected(false);
  };

  const handleAddressSelect = (address) => {
    handleAddressChange(address);
    setLocationSelected(true);
  };

  const handleSubmit = async () => {
    // Validate goal selection
    if (formData.selectedGoals.length === 0) {
      setGoalError('Please select at least one option to continue');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    setLoading(true);
    try {
      const currentData = {
        firstName:     formData.name     || null,
        gender:        formData.gender,
        lastName:      formData.lastName || null,
        address:       formData.location || null,
        selectedGoals: formData.selectedGoals.length > 0 ? formData.selectedGoals : null,
        photoUrl:      formData.photo    || null,
        dateOfBirth:   formData.dob
          ? new Date(formData.dob).toISOString().split('T')[0]
          : null,
      };

      const payload = Object.fromEntries(
        Object.entries(currentData).filter(([_, value]) => value !== null)
      );

      if (Object.keys(payload).length > 0) {
        await editProfile({ userId, payload });
      }

      // Business intent goes straight into creation — the dashboard menu in between
      // is where most business-intent signups were lost (Oct 2026 funnel).
      navigate(
        isBusiness ? '/company/new' : '/earlyuserdashboard',
        { state: { onboarding: false, fromOnboarding: isBusiness } }
      );

    } catch (error) {
      console.error('Error submitting form:', error);
    } finally {
      setLoading(false);
    }
  };

  // Mobile step content
  const renderMobileStepContent = () => {
    switch (currentMobileStep) {
      case 0:
        return (
          <GoalSelector
            selectedGoals={formData.selectedGoals}
            onSelect={handleGoalSelection}
            error={goalError}
          />
        );
      case 1:
        return (
          <Box>
            {/* Photo upload */}
            <Box sx={{ textAlign: 'center', mb: 3 }}>
              <Box sx={{ position: 'relative', width: 120, height: 120, mx: 'auto', mb: 1 }}>
                <Avatar src={profileImage} sx={{ width: 120, height: 120 }} />
                <IconButton
                  sx={{ position: 'absolute', bottom: 0, right: 0, backgroundColor: 'primary.main', '&:hover': { backgroundColor: 'primary.dark' } }}
                  component="label" size="large"
                >
                  <input hidden accept="image/*" type="file" onChange={handleImageUpload} />
                  <PhotoCameraIcon sx={{ color: 'white' }} />
                </IconButton>
              </Box>
              <Typography variant="body2" color="text.secondary">Add a profile photo</Typography>
            </Box>

            <TextField fullWidth label="First Name" value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              sx={{ mb: 3 }} />
            <TextField fullWidth label="Last Name" value={formData.lastName}
              onChange={(e) => setFormData(prev => ({ ...prev, lastName: e.target.value }))}
              sx={{ mb: 3 }} />

            <Typography variant="h6" gutterBottom>Your Location</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              For local recommendations and business connections
            </Typography>
            <PlacesAutocomplete value={formData.location || ''} onChange={handleAddressChange} onSelect={handleAddressSelect}>
              {({ getInputProps, suggestions, getSuggestionItemProps, loading }) => (
                <div>
                  <TextField {...getInputProps({ placeholder: 'Enter your location', fullWidth: true, variant: 'outlined' })} sx={{ mb: 2 }} />
                  {suggestions.length > 0 && (
                    <Paper elevation={3}>
                      {suggestions.map((suggestion) => {
                        const { key, ...suggestionProps } = getSuggestionItemProps(suggestion);
                        return (
                          <Box key={suggestion.placeId} {...suggestionProps}
                            sx={{ p: 2, cursor: 'pointer', borderBottom: '1px solid #e0e0e0', '&:hover': { bgcolor: 'grey.50' } }}>
                            <Typography variant="body2">{suggestion.description}</Typography>
                          </Box>
                        );
                      })}
                    </Paper>
                  )}
                </div>
              )}
            </PlacesAutocomplete>

            <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>Date of Birth <Typography component="span" variant="body2" color="text.secondary">(optional)</Typography></Typography>
            <DatePicker
              label="Date of Birth"
              value={formData.dob}
              onChange={(v) => setFormData(prev => ({ ...prev, dob: v }))}
              renderInput={(params) => <TextField {...params} fullWidth variant="outlined" sx={{ mb: 3 }} />}
            />

            <Typography variant="h6" gutterBottom>Gender <Typography component="span" variant="body2" color="text.secondary">(optional)</Typography></Typography>
            <TextField select fullWidth label="Select" value={formData.gender}
              onChange={(e) => setFormData(prev => ({ ...prev, gender: e.target.value }))} sx={{ mb: 3 }}>
              <MenuItem value="Male">Male</MenuItem>
              <MenuItem value="Female">Female</MenuItem>
              <MenuItem value="Other">Other</MenuItem>
            </TextField>
          </Box>
        );
      case 2:
        return (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <CheckCircleIcon sx={{ fontSize: 80, color: 'success.main', mb: 2 }} />
            <Typography variant="h5" gutterBottom>You are all set!</Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
              {isBusiness
                ? 'Ready to grow your business with AI'
                : 'Ready to discover trusted businesses'
              }
            </Typography>
            <Button
              variant="contained"
              size="large"
              onClick={handleSubmit}
              disabled={loading}
              sx={{ minWidth: 200, py: 1.5, mt: 2 }}
            >
              {loading ? 'Setting up...' : 'Get Started →'}
            </Button>
          </Box>
        );
      default:
        return null;
    }
  };

  const handleMobileNext = () => {
    if (currentMobileStep === 0 && formData.selectedGoals.length === 0) {
      setGoalError('Please select at least one option to continue');
      return;
    }
    if (currentMobileStep < 2) setCurrentMobileStep(prev => prev + 1);
    else handleSubmit();
  };

  const handleMobileBack = () => {
    if (currentMobileStep > 0) setCurrentMobileStep(prev => prev - 1);
  };

  const seoData = {
    title:       'Welcome to Revfer — Complete Your Setup',
    description: 'Set up your Revfer account to discover trusted businesses or grow your business with AI-powered content.'
  };

  if (loading || isPresignedUrlLoading) {
    return (
      <>
        <BasicSEO title="Setting Up Your Account - Revfer" description="Completing your Revfer profile setup." noIndex={true} />
        <Container maxWidth="xl" sx={{ pt: 3 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={3}><ProfilePreviewSkeleton /></Grid>
            <Grid item xs={12} md={6}><ProfilePreviewSkeleton /></Grid>
            <Grid item xs={12} md={3}><ProfilePreviewSkeleton /></Grid>
          </Grid>
        </Container>
      </>
    );
  }

  return (
    <>
      <BasicSEO title={seoData.title} description={seoData.description} noIndex={true} />

      <Box>
        {/* Welcome Banner */}
        <Paper elevation={0} sx={{
          width:      '100%',
          p:          isMobile ? 2 : 3,
          mb:         0,
          borderRadius: 0,
          background: 'linear-gradient(45deg, #1976d2, #1565c0)',
          color:      'white',
        }}>
          <Container maxWidth="xl">
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Typography variant={isMobile ? 'h4' : 'h3'} gutterBottom sx={{ color: 'white', fontWeight: 500 }}>
                  Welcome to Revfer!
                </Typography>
                <Typography variant={isMobile ? 'body1' : 'h6'} sx={{ color: 'rgba(255,255,255,0.9)', fontWeight: 400 }}>
                  {formData.selectedGoals.length === 0
                    ? 'Tell us what brings you here'
                    : isBusiness
                      ? 'Let\'s get your business set up'
                      : 'Let\'s get you discovering'
                  }
                </Typography>
              </Box>
              {isMobile && (
                <IconButton color="inherit" onClick={() => setSidebarOpen(true)} sx={{ ml: 2 }}>
                  <MenuIcon />
                </IconButton>
              )}
            </Box>
          </Container>
        </Paper>

        <Container maxWidth="xl" sx={{ pt: 3, px: { xs: 2, sm: 3 } }}>
          {isMobile ? (
            <>
              <MobileStepProgress currentStep={currentMobileStep} />

              <Paper variant="outlined" sx={{ p: 3, border: 'none', mb: 3 }}>
                {renderMobileStepContent()}

                {currentMobileStep < 2 && (
                  <Box sx={{ mt: 4, display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                    <Button onClick={handleMobileBack} disabled={currentMobileStep === 0} size="large" sx={{ minWidth: 100 }}>
                      Back
                    </Button>
                    <Button variant="contained" onClick={handleMobileNext} size="large" sx={{ minWidth: 120 }}>
                      {currentMobileStep === 1 ? 'Review' : 'Next'}
                    </Button>
                  </Box>
                )}
              </Paper>

              <Fab color="primary" sx={{ position: 'fixed', bottom: 16, right: 16, zIndex: 1000 }}
                onClick={() => setSidebarOpen(true)}>
                <InfoIcon />
              </Fab>

              <MobileInfoSidebar
                isOpen={sidebarOpen}
                onClose={() => setSidebarOpen(false)}
                profileImage={profileImage}
                formData={formData}
                navigate={navigate}
                isBusiness={isBusiness}
              />
            </>
          ) : (
            // ===== Desktop Layout =====
            <Grid container spacing={3}>

              {/* Left Column — reacts to goal */}
              <Grid item xs={12} md={3}>
                <CapabilitiesPanel
                  isBusiness={isBusiness}
                  profileImage={profileImage}
                  formData={formData}
                  handleImageUpload={handleImageUpload}
                />
              </Grid>

              {/* Middle Column — form */}
              <Grid item xs={12} md={6}>
                <Paper variant="outlined" sx={{ p: 3, border: 'none' }}>

                  {/* Goals — first */}
                  <GoalSelector
                    selectedGoals={formData.selectedGoals}
                    onSelect={handleGoalSelection}
                    error={goalError}
                  />

                  {/* Name */}
                  <TextField fullWidth label="First Name" value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    sx={{ mb: 3 }} />
                  <TextField fullWidth label="Last Name" value={formData.lastName}
                    onChange={(e) => setFormData(prev => ({ ...prev, lastName: e.target.value }))}
                    sx={{ mb: 4 }} />

                  {/* Location */}
                  <Typography variant="h6" gutterBottom>Location</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    For local recommendations and business connections
                  </Typography>
                  <PlacesAutocomplete value={formData.location || ''} onChange={handleAddressChange} onSelect={handleAddressSelect}>
                    {({ getInputProps, suggestions, getSuggestionItemProps, loading }) => (
                      <div>
                        <TextField {...getInputProps({ placeholder: 'Enter your location', fullWidth: true, variant: 'outlined' })} sx={{ mb: 2 }} />
                        {suggestions.length > 0 && (
                          <Paper>
                            {loading && <div>Loading...</div>}
                            {suggestions.map((suggestion) => {
                              const { key, ...suggestionProps } = getSuggestionItemProps(suggestion);
                              return (
                                <div key={suggestion.placeId} {...suggestionProps}
                                  style={{ padding: '10px', cursor: 'pointer', borderBottom: '1px solid #e0e0e0' }}>
                                  {suggestion.description}
                                </div>
                              );
                            })}
                          </Paper>
                        )}
                      </div>
                    )}
                  </PlacesAutocomplete>

                  {/* DOB — optional */}
                  <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                    Date of Birth{' '}
                    <Typography component="span" variant="body2" color="text.secondary">(optional)</Typography>
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Helps us offer more personalized insights
                  </Typography>
                  <DatePicker
                    label="Date of Birth"
                    value={formData.dob}
                    onChange={(v) => setFormData(prev => ({ ...prev, dob: v }))}
                    renderInput={(params) => <TextField {...params} fullWidth variant="outlined" />}
                  />

                  {/* Gender — optional */}
                  <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                    Gender{' '}
                    <Typography component="span" variant="body2" color="text.secondary">(optional)</Typography>
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Improves relevance of AI insights tailored to your needs
                  </Typography>
                  <TextField select fullWidth label="Select" value={formData.gender}
                    onChange={(e) => setFormData(prev => ({ ...prev, gender: e.target.value }))}
                    sx={{ mb: 4 }}>
                    <MenuItem value="Male">Male</MenuItem>
                    <MenuItem value="Female">Female</MenuItem>
                    <MenuItem value="Other">Other</MenuItem>
                  </TextField>

                  <Button
                    variant="contained"
                    size="large"
                    onClick={handleSubmit}
                    disabled={loading || formData.selectedGoals.length === 0}
                    sx={{ minWidth: 200 }}
                  >
                    {loading ? 'Setting up...' : 'Get Started →'}
                  </Button>

                  {formData.selectedGoals.length === 0 && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                      Please select what brings you to Revfer above
                    </Typography>
                  )}
                </Paper>
              </Grid>

              {/* Right Column — reacts to goal */}
              <Grid item xs={12} md={3}>
                <JourneyPanel isBusiness={isBusiness} />
              </Grid>

            </Grid>
          )}
        </Container>
      </Box>
    </>
  );
}